import { IEffectModelValCollection, IGlowModelVal } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import {
    alphaToEmu, alphaToOpacity, emuToAlpha, emuToPt, opacityToAlpha, ptToEmu,
} from '@src/slide/infrastructure/module/independent/unit';
import { ICTEffectList, ICTGlowEffect, IEGColorChoice, DeltaOf } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import _set from 'lodash/set';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import {
    ColorConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';

/**
 * 效果 - 发光
 */
export class GlowConverter {
    /**
     * 发光的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTEffectList>>,
        uiData: IEffectModelValCollection,
    ): void {
        if (sdkData.glow) {
            const { color, opacity } = GlowConverter.convertColorFromSdkToUi(sdkData.glow);
            const size = GlowConverter.convertSizeFromSdkToUi(sdkData.glow);
            const egColorChoice = sdkData.glow.egColorChoice as DeepReadonly<DeepPartial<IEGColorChoice>>;
            const tdColor = ColorConverter.convertPartialDataFromSdkToUi(egColorChoice);

            // todo: opacity和color整合到tdColor
            uiData.glow = {
                color,
                tdColor,
                opacity,
                size,
            };
            // 清掉值是undefined的属性
            Object.keys(uiData.glow || {}).forEach((p) => {
                if (uiData.glow && uiData.glow[p] === undefined) {
                    delete uiData.glow[p];
                }
            });
        } else {
            delete uiData.glow;
        }
    }

    /**
     * 发光的ui-> sdk 映射
     * @param uiData
     * @param sdkData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IEffectModelValCollection>,
        sdkData: DeltaOf<ICTEffectList>,
    ): void {
        if (uiData.glow) {
            const egColorChoice = {};
            const tdColor = uiData.glow.tdColor as DeepReadonly<DeepPartial<ITDColor>>;
            ColorConverter.convertPartialDataFromUiToSdk(tdColor, egColorChoice);
            sdkData.glow = {
                // egColorChoice: GlowConverter.convertColorFromUiToSdk(uiData.glow),
                egColorChoice,
                rad: GlowConverter.convertSizeFromUiToSdk(uiData.glow),
            };
        } else if (uiData.glow === null) {
            sdkData.glow = null;
        }
    }

    // color/opacity
    private static convertColorFromSdkToUi(sdkData?: DeepReadonly<DeepPartial<ICTGlowEffect>> | null): {
        color: string;
        opacity: number;
    } {
        const rgbColor = sdkData?.egColorChoice?.srgbClr?.val;
        const alpha = sdkData?.egColorChoice?.srgbClr?.egColorTransform?.[0]?.alpha?.val;
        if (rgbColor) {
            return {
                color: rgbColor?.startsWith('#') ? rgbColor : `#${rgbColor}`,
                opacity: alpha === undefined ? 0 : alphaToOpacity(emuToAlpha(alpha)),
            };
        }
        return { color: '#000000', opacity: 0 };
    }

    // size
    private static convertSizeFromSdkToUi(sdkData?: DeepReadonly<DeepPartial<ICTGlowEffect>> | null): number {
        if (sdkData?.rad || sdkData?.rad === 0) {
            return emuToPt(sdkData.rad);
        }
        return 0;
    }

    // color/opacity
    private static convertColorFromUiToSdk(uiData?:
    DeepPartial<IGlowModelVal> | null): IEGColorChoice | undefined {
        if (uiData?.color || (uiData?.opacity || uiData?.opacity === 0)) {
            const egColor: IEGColorChoice = {};
            _set(egColor, 'srgbClr.val', uiData.color?.replace(/#/, ''));
            _set(egColor, 'srgbClr.egColorTransform[0].alpha.val', alphaToEmu(opacityToAlpha(uiData.opacity ?? 0)));
            return egColor;
        }
    }

    // size
    private static convertSizeFromUiToSdk(uiData?:
    DeepPartial<IGlowModelVal> | null): number | undefined {
        if (uiData?.size || uiData?.size === 0) {
            return ptToEmu(uiData.size);
        }
        if (uiData?.size === null) {
            return 0;
        }
    }
}
